<template>
  <SearchableSelect
    v-model="selectedScopesComputed"
    :items="scopeItems"
    return-object
    :loading="loading"
    :label="t(selectedViewBy)"
    :multiple="!singleSelect"
    :customToggleAllFn="toggleAll"
    :itemTitle="itemTitle"
    :rules="rules"
    :showSelectedAtTop="showSelectedAtTop"
  />
</template>

<script setup>
import { computed, watch, toRefs } from 'vue';
import { useRootStore } from '../../../../stores/root';
import { storeToRefs } from 'pinia';
import SearchableSelect from '../../../SearchableSelect.vue';
import { useI18n } from 'vue-i18n';
const { t } = useI18n();

const props = defineProps({
  singleSelect: {
    type: Boolean,
    default: false,
  },
  maxLocations: {
    type: Number,
  },
  onlyActiveLocations: {
    type: Boolean,
    default: true,
  },
  showSelectedAtTop: {
    type: Boolean,
    default: true,
  },
});
const { singleSelect, maxLocations, onlyActiveLocations, showSelectedAtTop } =
  toRefs(props);

const store = useRootStore();
const { locations, patches, hierarchyByUser, selectedScopes, selectedViewBy } =
  storeToRefs(store);

const selectedScopesComputed = computed({
  get() {
    if (singleSelect.value) {
      return selectedScopes.value[0];
    }
    return selectedScopes.value;
  },
  set(v) {
    const value = singleSelect.value ? [v] : v;
    selectedScopes.value = value;
  },
});

const loading = computed(() => {
  if (selectedViewBy.value === 'patches' && !patches.value) return true;
  return false;
}, 500);

const filteredLocations = computed(() => {
  if (!locations.value) return [];
  const mappedLocations = [...locations.value].sort(
    (a, b) => a.value - b.value,
  );
  if (!onlyActiveLocations.value) {
    return mappedLocations;
  }
  return mappedLocations.filter((loc) => loc.isActive);
});

const filteredPatches = computed(() => {
  if (!patches.value) return [];
  if (!onlyActiveLocations.value) return patches.value;
  return patches.value.reduce((acc, patch) => {
    const newPatch = {
      ...patch,
      locations: patch.locations.filter((nsn) =>
        filteredLocations.value.find((loc) => loc.value === nsn),
      ),
    };
    if (newPatch.locations.length) acc.push(newPatch);
    return acc;
  }, []);
});

const scopeItems = computed(() => {
  if (!selectedViewBy.value) return [];
  if (selectedViewBy.value === 'locations' || singleSelect.value)
    return filteredLocations.value;
  if (selectedViewBy.value === 'patches') return filteredPatches.value;

  // hierarchy
  const selectedHierarchy = hierarchyByUser.value?.find(
    (role) => role.text === selectedViewBy.value,
  );
  if (selectedHierarchy) {
    const users = selectedHierarchy.users.reduce((acc, user) => {
      const userFilteredLocations = [];
      const userLocations = user.locations.reduce((acc, loc) => {
        const location = filteredLocations.value.find(
          (location) => location.value === loc,
        );
        if (location) {
          acc.push(loc);
        } else {
          userFilteredLocations.push(loc);
        }
        return acc;
      }, []);
      if (user.label) {
        acc.push({
          name: user.label,
          locations: userLocations,
          isFiltered: userFilteredLocations.length > 0,
          filteredLocations: userFilteredLocations,
        });
      }
      return acc;
    }, []);
    return users;
  }
  return [];
});

const itemTitle = computed(() => {
  if (selectedViewBy.value === 'locations') return 'nameValue';
  return 'name';
});

watch(selectedViewBy, () => {
  if (selectedViewBy.value === 'locations') toggleAll();
  else selectedScopesComputed.value = [];
});

watch(scopeItems, () => {
  if (selectedViewBy.value !== 'locations') return;
  if (singleSelect.value && !selectedScopesComputed.value) {
    toggleAll();
  }
  if (!singleSelect.value && !selectedScopesComputed.value.length) {
    toggleAll();
  }
});

function toggleAllLocations() {
  if (singleSelect.value) {
    selectedScopesComputed.value =
      scopeItems.value.find((x) => x.isActive) || scopeItems.value[0];
    return;
  }
  const activeLocations = scopeItems.value.filter((x) => x.isActive);
  if (selectedScopesComputed.value.length === activeLocations.length) {
    selectedScopesComputed.value = [];
    return;
  }
  if (!maxLocations.value) {
    selectedScopesComputed.value = scopeItems.value.filter((x) => x.isActive);
    return;
  }

  selectedScopesComputed.value = scopeItems.value
    .filter((x) => x.isActive)
    .slice(0, maxLocations.value);
}

function toggleAllPatches() {
  // patches can never be shown when singleSelect is true
  if (!maxLocations.value) {
    if (selectedScopesComputed.value.length === scopeItems.value.length) {
      selectedScopesComputed.value = [];
    } else {
      selectedScopesComputed.value = scopeItems.value;
    }
  } else {
    if (selectedScopesComputed.value.length) {
      selectedScopesComputed.value = [];
      return;
    }
    selectedScopesComputed.value = scopeItems.value.reduce((scopes, scope) => {
      const currentScopes = scopes.reduce((prev, curr) => {
        prev.push(...curr.value);
        return prev;
      }, []);
      const scopeSet = new Set(currentScopes);
      if (maxLocations.value > scopeSet.size + scope.value.length) {
        scopes.push(scope);
      }
      return scopes;
    }, []);
  }
}

function toggleAll() {
  if (selectedViewBy.value === 'locations') {
    toggleAllLocations();
  } else {
    toggleAllPatches();
  }
}
watch(
  singleSelect,
  () => {
    // force viewby to loc when single select
    if (singleSelect.value) {
      if (selectedViewBy.value !== 'locations') {
        selectedViewBy.value = 'locations';
      }
      const scope = selectedScopes.value[0] || scopeItems.value[0];
      if (scope) selectedScopesComputed.value = scope;
    } else if (!selectedScopesComputed.value.length) {
      toggleAll();
    }
  },
  { immediate: true },
);

const rules = [
  (v) => {
    if (maxLocations.value && v.length > maxLocations.value) {
      return `${t('you-can-only-select')} ${maxLocations.value} ${t('locs')}`;
    }
    if (singleSelect.value && !v) {
      return t('please-select-a-location');
    }
    if (!singleSelect.value && !v.length) {
      return t('please-select-at-least-one-location');
    }
    return true;
  },
];
</script>
<i18n>
{
  "en": {
    "locations": "Locations",
    "patches": "Patches",
    "please-select-a-location": "Please select a location",
    "please-select-at-least-one-location": "Please select at least one location",
    "you-can-only-select": "You can only select",
    "locs": "locations",
  },
  "es": {
    "locations": "Ubicaciones",
    "patches": "Grupos",
    "please-select-a-location": "Por favor selecciona una ubicación",
    "please-select-at-least-one-location": "Por favor selecciona al menos una ubicación",
    "you-can-only-select": "Solo puedes seleccionar",
    "locs": "ubicaciones",
  }
}
</i18n>
<style scoped>
.no-wrap {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}
:deep(.v-input__details) {
  display: none;
}
</style>
